<script>
    import { afterUpdate } from 'svelte';
	import { Chart, registerables } from 'chart.js';
    import annotationPlugin from 'chartjs-plugin-annotation';
    import { Tooltip } from 'sveltestrap';

    import { graphDataMapKeys, createGraphDataMap, createOverallChartDatasets, createIndividualChartDatasets, getStartDate, getFinishDate } from './inmate-graph.js'
    import { getChartAnnotations } from './inmate-graph-annotation';
    import { getDisplayType } from '../trigger-date.js';
    import { vptServer, token, inmateDrawerOpen, selectedDateRange, standardDateRanges, selectedInmateDateArrived, selectedInmateDateDeparted } from '../stores.js';
    import { getApiDateFormat, getIsoDateStringAsDate } from '../date-time.js';
    import { sortByProperty } from '../sort.js';
    import { referenceDataRegimeLevelList } from '../reference-data.js';

    import Dropdown from '../component/Dropdown.svelte';
    import FilterButton from '../component/FilterButton.svelte';

    export let id;
    export let todaysConfig;

    const YEAR_ON_YEAR = -1;
    
    const dateRanges = [
        { value : todaysConfig.isolationPastDaysToAnalyse, label : 'Analysis Period (' + todaysConfig.isolationPastDaysToAnalyse + ' days)'},
        ...standardDateRanges,
        { value : YEAR_ON_YEAR, label : 'Year on Year'}
    ];

    let history = [];
    let graphData = new Map();
    let selectedGraph = graphDataMapKeys.OVERALL;
    let datasetActiveStatus = [];

    let balanceHistory = [];
    let isBalanceDataDisplayed = false;

    let surveyHistory = [];
    let isSurveyDataDisplayed = true;

    let triggerDates = [];
    let isTriggerDatesDisplayed = true;
    let isChangeDatesDisplayed = true;
    let isDischargesDisplayed = false;
    let controlShowAnnotationLabels = true;

    let preventAutorender = true;
    let searchCompleted = false;

    let chart;
    Chart.register(...registerables);
    Chart.register(annotationPlugin);
    let legendDefaultClick = Chart.defaults.plugins.legend.onClick;

    $: {
        if (todaysConfig) {
            history = null;
            balanceHistory = null;
            surveyHistory = null;
            triggerDates = null;
            searchCompleted = false;
            
            // If the selected isolation date range is not an available option on this view then default to Analysis Period
            if ($selectedDateRange == 18000) {
                selectedDateRange.set(todaysConfig.isolationPastDaysToAnalyse);
            }

            getTriggerDates();
        }
    }

    async function getTriggerDates() {
        await fetch(`${$vptServer}/inmate/${id}/trigger-dates`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            let currentYear = new Date().getFullYear();
            triggerDates = data.map(triggerDate => {
                let triggerDateAnnual = getIsoDateStringAsDate(triggerDate.date);
                triggerDateAnnual.setFullYear(currentYear);
                return {...triggerDate, 
                        annualDate: triggerDateAnnual,
                        displayType: getDisplayType(triggerDate.type)};
            });
            triggerDates.sort(sortByProperty("annualDate", false));
            getSurveyHistory();
        })
        .catch(error => {
            console.error("Error getting inmate history (trigger dates)");
            console.error(error);
            return;
        });
    }

    async function getSurveyHistory() {
        await fetch(`${$vptServer}/inmate/${id}/survey/list`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            // Remove any surveys completed before the selected graph start date
            let graphStartDate = getStartDate($selectedDateRange, $selectedInmateDateArrived)
            graphStartDate.setHours(0,0,0,0);

            surveyHistory = [];
            data.forEach((item) => {
                if (getIsoDateStringAsDate(item.dateSurvey) >= graphStartDate) {
                    surveyHistory.push(item);
                }
            });

            getBalanceHistory();
        })
        .catch(error => {
            console.error("Error getting inmate history (survey)");
            console.error(error);
            return;
        });
    }

    async function getBalanceHistory() {
        await fetch(`${$vptServer}/inmate/${id}/balance/list?startDate=${getApiDateFormat(getStartDate($selectedDateRange, $selectedInmateDateArrived))}&finishDate=${getApiDateFormat(getFinishDate($selectedInmateDateDeparted))}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            balanceHistory = data;
            getInmateHistory();
        })
        .catch(error => {
            console.error("Error getting inmate history (balance)");
            console.error(error);
            return;
        });
    }
    
    async function getInmateHistory() {
        searchCompleted = false;
        history = [];
        graphData = new Map();
        
        if (!preventAutorender) {
           try {
               chart.destroy();
           } catch { }
           preventAutorender = true;
        }

        await fetch(`${$vptServer}/inmate/${id}/isolation/list?startDate=${getApiDateFormat(getStartDate($selectedDateRange, $selectedInmateDateArrived))}&finishDate=${getApiDateFormat(getFinishDate($selectedInmateDateDeparted))}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            history = data;
        })
        .catch(error => {
            console.error("Error getting inmate history (isolation)");
            console.error(error);
            return;
        });

        if (history.length > 0) {
            // Create a map containing all data needed for the inmate graph
            let futureDays = (!$selectedInmateDateDeparted) ? todaysConfig.triggerDatesFutureDaysToAnalyse : 0;
            graphData = createGraphDataMap($selectedDateRange == YEAR_ON_YEAR, history, surveyHistory, balanceHistory, futureDays);

            // For the Year on Year view, also populate an additional map to track the display status of each dataset
            if ($selectedDateRange == YEAR_ON_YEAR) {
                let datasetActiveStatus = [];
                for (let i = 0; i < graphData.size; i++) {
                    datasetActiveStatus.push(false);
                }
            }

            // Force the Overall view when Year on Year range is selected
            if ($selectedDateRange == YEAR_ON_YEAR) {
                selectedGraph = graphDataMapKeys.OVERALL;
            }
            
            // Transform the graph data map into datasets and render the appropriate chart (overall or individual element)
            if (selectedGraph === graphDataMapKeys.OVERALL) {
                renderOverallChart(createOverallChartDatasets($selectedDateRange == YEAR_ON_YEAR, graphData, datasetActiveStatus,
                    isSurveyDataDisplayed, isBalanceDataDisplayed, todaysConfig.surveyLabelName));
            } else {
                renderIndividualChart(createIndividualChartDatasets(graphData, selectedGraph, isBalanceDataDisplayed));
            }
        }

        // Allow filter changes to update the graph
        preventAutorender = false;
        searchCompleted = true;
    }

    // Each time the graph display parameters are changed, the chart must be destroyed and rendered from scratch
    afterUpdate(() => {
        // The preventAutorender check, stops the afterUpdate method from being called before any data has been retrieved
        if (!preventAutorender) {
            try {
                chart.destroy();
            } catch { }
            
            // Force the Overall view when Year on Year range is selected
            if ($selectedDateRange == YEAR_ON_YEAR) {
                selectedGraph = graphDataMapKeys.OVERALL;
            }

            // Transform the graph data map into datasets and render the appropriate chart (overall or individual element)
            if (selectedGraph === graphDataMapKeys.OVERALL) {
                renderOverallChart(createOverallChartDatasets($selectedDateRange == YEAR_ON_YEAR, graphData, datasetActiveStatus,
                    isSurveyDataDisplayed, isBalanceDataDisplayed, todaysConfig.surveyLabelName));
            } else {
                renderIndividualChart(createIndividualChartDatasets(graphData, selectedGraph, isBalanceDataDisplayed));
            }
        }
    });

    function renderOverallChart(datasets) {
        let futureDays = (!$selectedInmateDateDeparted) ? todaysConfig.triggerDatesFutureDaysToAnalyse : 0;

        if (searchCompleted && history.length > 0) {
            var ctx = document.getElementById("historyChart").getContext("2d");
            chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets: datasets
                },
                options: {
                    animation: {
                        duration: 0
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            position: 'left',
                            min: 0,
                            max: 100,
                            grid: {
                                display: false
                            },
                            ticks: {
                                callback: function (value) {
                                    if (value === 0) return "Lower Risk";
                                    if (value === 100) return "Higher Risk";
                                }
                            }
                        },
                        y1: {
                            display: !($selectedDateRange === YEAR_ON_YEAR) && isBalanceDataDisplayed,
                            position: 'right',
                            min: 0,
                            grid: {
                                drawOnChartArea: false
                            },
                            ticks: {
                                callback: function (value) {
                                    return "£" + value;
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            display: ($selectedDateRange === YEAR_ON_YEAR),
                            onClick: (evt, legendItem, legend) => {
                                legendDefaultClick(evt, legendItem, legend);
                                datasetActiveStatus[legendItem.datasetIndex] = !(datasetActiveStatus[legendItem.datasetIndex]);
                            }
                        },
                        tooltip: {
                            displayColors: false,
                            callbacks: {
                                label: function(context) {
                                    if (context.dataset.yAxisID == 'y1') {
                                        return context.dataset.label + ": £" + context.parsed.y;
                                    } else { 
                                        return context.dataset.label + ": " + Math.round(context.parsed.y) + "%";
                                    }
                                }
                            }
                        },
                        annotation: {
                            // Render different annotations based on the selected graph
                            annotations: getChartAnnotations($selectedDateRange === YEAR_ON_YEAR, futureDays,
                                isTriggerDatesDisplayed, triggerDates, isChangeDatesDisplayed, history, isDischargesDisplayed, controlShowAnnotationLabels,
                                document.getElementById('history-chart-card').offsetWidth, $referenceDataRegimeLevelList, todaysConfig.numberOfDaysToShowTemporaryDischarge)
                        }
                    }
                }
            });
        }
    }

    function renderIndividualChart(datasets) {
        let futureDays = (!$selectedInmateDateDeparted) ? todaysConfig.triggerDatesFutureDaysToAnalyse : 0;
        if (searchCompleted && history.length > 0) {
            var ctx = document.getElementById("historyChart").getContext("2d");
            chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets: datasets
                },
                options: {
                    animation: {
                        duration: 0
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            min: 0,
                            max: 3,
                            grid: {
                                display: false
                            },
                            ticks: {
                                callback: function (value) {
                                    if (value === 3) return "High";
                                    if (value === 2) return "Medium";
                                    if (value === 1) return "No Data";
                                    if (value === 0.5) return "No Regime";
                                    if (value === 0) return "Low";
                                }
                            }
                        },
                        y1: {
                            display: isBalanceDataDisplayed,
                            position: 'right',
                            min: 0,
                            grid: {
                                drawOnChartArea: false
                            },
                            ticks: {
                                callback: function (value) {
                                    return "£" + value;
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            displayColors: false,
                            callbacks: {
                                label: function (context) {
                                    if (context.parsed.y === 3) return "High";
                                    if (context.parsed.y === 2) return "Medium";
                                    if (context.parsed.y === 1) return "No Data";
                                    if (context.parsed.y === 0.5) return "No Regime";
                                    if (context.parsed.y === 0) return "Low";
                                }
                            }
                        },
                        annotation: {
                            annotations: getChartAnnotations(false, futureDays,
                                isTriggerDatesDisplayed, triggerDates, isChangeDatesDisplayed, history, isDischargesDisplayed, controlShowAnnotationLabels,
                                document.getElementById('history-chart-card').offsetWidth, $referenceDataRegimeLevelList, todaysConfig.numberOfDaysToShowTemporaryDischarge)
                        }
                    }
                }
            });
        }
    }
</script>

<style>
    .container-fluid {
        min-width: 1100px;
        height: 100%;
        padding-top: 0;
    }

    .container-fluid {
        padding-right: 96px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .container-fluid-short {
        padding-right: 270px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }
    
    .table-filters {
        position: fixed;
        top: 213px;
        width: 100%;
        min-width: 1200px;
        padding: 3px 15px 10px 15px;
    }

    .table-filters .form-inline {
        justify-content: flex-start;
    }

    #history-chart-container, #alert-container, .spinner-container {
        height: 100%;
        padding-top: 211px;
    }

    #history-chart-container .card {
        height: 98%;
        margin-bottom: 0;
    }

    :global(.btn-primary.btn-isolation, .btn-primary.btn-isolation:hover, .btn-primary.btn-isolation:focus, .btn-primary.btn-isolation:active) {
        background-color: #3e1d60;
        border-color: #3e1d60;
    }

    :global(.btn-outline-primary.btn-isolation, .btn-outline-primary.btn-isolation:hover, .btn-outline-primary.btn-isolation:focus, .btn-outline-primary.btn-isolation:active) {
        background-color: white;
        color: #3e1d60;
        border-color: #3e1d60;
    }

    #overlays {
        height: 34px;
        display: flex;
        background-color: white;
        border: 1px solid #ced4da;
        border-radius: .25rem;
    }

    #trends, #events {
        display: flex;
        padding-left: 10px;
    }

    #trends {
        border-right: 1px solid #ced4da;
    }

    #overlays input[type="checkbox"] {
        width: 14px;
        height: 14px;
        margin-right: 4px;
    }

    #survey-overlay {
        accent-color: #00836B;
    }

    #balance-overlay {
        accent-color: #C2185B;
    }

    #trigger-date-overlay {
        accent-color: #0097B5;
    }
    
    #changes-overlay {
        accent-color: #CE6924;   
    }

    #discharges-overlay {
        accent-color: #7b7e80;   
    }

    #events button {
        margin-right: 10px;
    }

    #show-labels {
        font-size: 16px;
        line-height: 24px;
    }
</style>

{#if searchCompleted}
    <div class="table-filters">
        <div class="form-inline" role="toolbar" aria-label="Graph data sets">
            <Dropdown bind:value={$selectedDateRange} id="date-range" options={dateRanges} classes="mr-3" />

            <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.OVERALL}} id="filter-overall"
                    isActive={selectedGraph == graphDataMapKeys.OVERALL} title="Overall Isolation Level"
                    classes={($selectedDateRange == YEAR_ON_YEAR) ? "btn-isolation mr-3" : "btn-isolation mr-1"} >
                Isolation
            </FilterButton>

            {#if history.length > 0 && !($selectedDateRange == YEAR_ON_YEAR)}
                <div class="btn-group mr-3" role="group" aria-label="Select data view">       
                    {#if !(todaysConfig.isolationVisitWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.VISITS}} id="filter-visits"
                            isActive={selectedGraph == graphDataMapKeys.VISITS} classes="btn-isolation">Visits</FilterButton>
                    {/if}

                    {#if !(todaysConfig.isolationWorkWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.WORK}} id="filter-work"
                            isActive={selectedGraph == graphDataMapKeys.WORK} classes="btn-isolation">Work</FilterButton>
                    {/if}

                    {#if !(todaysConfig.isolationPhoneWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.PHONE}} id="filter-phone"
                            isActive={selectedGraph == graphDataMapKeys.PHONE} classes="btn-isolation">Phone</FilterButton>
                    {/if}

                    {#if !(todaysConfig.isolationMealWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.MEALS}} id="filter-meals"
                            isActive={selectedGraph == graphDataMapKeys.MEALS} classes="btn-isolation">Meals</FilterButton>
                    {/if}

                    {#if !(todaysConfig.isolationCanteenWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.CANTEEN}} id="filter-canteen"
                                    isActive={selectedGraph == graphDataMapKeys.CANTEEN} classes="btn-isolation">Canteen</FilterButton>
                    {/if}

                    {#if !(todaysConfig.isolationGymWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.GYM}} id="filter-gym"
                            isActive={selectedGraph == graphDataMapKeys.GYM} classes="btn-isolation">Gym</FilterButton>
                    {/if}

                    {#if !(todaysConfig.isolationPaWeighting === 0)}
                        <FilterButton on:click={()=> {selectedGraph = graphDataMapKeys.ACTIVITIES}} id="filter-activities"
                            isActive={selectedGraph == graphDataMapKeys.ACTIVITIES} classes="btn-isolation">Activities</FilterButton>
                    {/if}
                </div>
            {/if}

            <div id="overlays">
                {#if !($selectedDateRange == YEAR_ON_YEAR) && 
                    ((surveyHistory.length > 1 && selectedGraph == graphDataMapKeys.OVERALL) || (balanceHistory.length > 1))}
                    
                    <div id="trends">
                        {#if surveyHistory.length > 1 && selectedGraph == graphDataMapKeys.OVERALL}
                            <div class="form-check form-check-inline">
                                <input type="checkbox" bind:checked={isSurveyDataDisplayed} id="survey-overlay" />
                                <label for="survey-overlay">{todaysConfig.surveyLabelName}</label>
                            </div>
                        {/if}

                        {#if balanceHistory.length > 1}
                            <div class="form-check form-check-inline">
                                <input type="checkbox" bind:checked={isBalanceDataDisplayed} id="balance-overlay" />
                                <label for="balance-overlay">Balance</label>
                            </div>
                        {/if}
                    </div>
                {/if}

                <div id="events">
                    <div class="form-check form-check-inline">
                        <input type="checkbox" bind:checked={isTriggerDatesDisplayed} id="trigger-date-overlay" />
                        <label for="trigger-date-overlay">{todaysConfig.triggerDatesLabelName}</label>
                    </div>
    
                    {#if !($selectedDateRange == YEAR_ON_YEAR)}
                        <div class="form-check form-check-inline">
                            <input type="checkbox" bind:checked={isChangeDatesDisplayed} id="changes-overlay" />
                            <label for="changes-overlay">Changes</label>
                        </div>

                        <div class="form-check form-check-inline">
                            <input type="checkbox" bind:checked={isDischargesDisplayed} id="discharges-overlay" />
                            <label for="discharges-overlay">Temporary Discharges</label>
                        </div>
                    {/if}

                    {#if isTriggerDatesDisplayed || (isChangeDatesDisplayed && !($selectedDateRange == YEAR_ON_YEAR))}
                        <button on:click={()=> {controlShowAnnotationLabels = !controlShowAnnotationLabels}}
                                type="button" class="button-reset focus-outline" id="filter-label">
                            <span id="show-labels" class:text-link={controlShowAnnotationLabels}><i class="bi bi-card-text" /></span>
                        </button>
                        <Tooltip target="filter-label" placement="bottom">
                            Show Labels
                        </Tooltip>
                    {/if}
                </div>                
            </div>
        </div>
    </div>

    <div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
        {#if history.length > 0}
            <div id="history-chart-container" class="row">
                <div class="col-sm-12">
                    <div class="card">
                        <div id="history-chart-card" class="card-body">
                            <canvas id="historyChart"></canvas>
                        </div>
                    </div>
                </div>
            </div>
        {:else}
            <div id="alert-container">
                <div class="alert alert-danger" role="alert">No records were found for the selected date period. Please try selecting a different period.</div>
            </div>
        {/if}
    </div>
{:else}
    <div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
        <div class="spinner-container">
            <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
            <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
        </div>
    </div>
{/if}